import { Fragment, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { DateRange } from "react-day-picker";
import { ptBR } from "date-fns/locale";
import { format } from "date-fns";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { cn } from "@/lib/utils";
import { ChevronDown, RefreshCw, AlertCircle } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

import { Skeleton } from "@/components/ui/skeleton";
import { useEffect } from "react";
import { formatPhone } from "@/lib/utils";
import { toast } from "sonner";

const CHATWOOT_BASE_URL = typeof window !== "undefined" && (window as any).__CHATWOOT_BASE_URL__
  ? (window as any).__CHATWOOT_BASE_URL__
  : import.meta.env.VITE_CHATWOOT_BASE_URL || "https://app.chatwoot.com";

const entityLabels: Record<string, string> = {
  client: "Cliente",
  supplier: "Fornecedor",
  profile: "Usuário",
};

const actionLabels: Record<string, string> = {
  create: "Criação",
  update: "Atualização",
  delete: "Remoção",
  webhook: "Webhook",
  reconcile: "Reprocesso",
};

const statusVariants: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  success: { label: "Sucesso", variant: "default" },
  error: { label: "Erro", variant: "destructive" },
  ignored: { label: "Ignorado", variant: "secondary" },
};

const directionLabels: Record<string, string> = {
  outbound: "Saída",
  webhook: "Webhook",
  reconcile: "Reprocesso",
};

type EventRow = {
  id: string;
  created_at: string;
  tenant_id: string | null;
  entity_type: "client" | "supplier" | "profile" | null;
  entity_id: string | null;
  chatwoot_contact_id: number | null;
  action: string | null;
  direction: string | null;
  status: "success" | "error" | "ignored";
  payload: any;
  error_message: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  chatwootAccountId?: number | null;
};

type TenantOption = { value: string; label: string };

type Filters = {
  tenantId?: string | undefined;
  entityType?: string;
  action?: string;
  direction?: string;
  status?: string;
  search?: string;
  dateRange?: DateRange;
};

function useTenantsList(enabled: boolean) {
  return useQuery({
    queryKey: ["tenants-options"],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenants" as any)
        .select("id,name")
        .order("name");
      if (error) throw error;
      return (data || []).map((t: any) => ({ value: t.id, label: t.name })) as TenantOption[];
    },
  });
}

const formatPhoneDisplay = (value?: string | null) => {
  if (!value) return null;
  const digitsOnly = value.replace(/\D/g, "");
  if (!digitsOnly) return value;

  let normalized = digitsOnly;
  if (normalized.startsWith("55") && normalized.length > 11) {
    normalized = normalized.slice(2);
  }

  const formatted = formatPhone(normalized);
  if (formatted) return formatted;

  return value;
};

const getDiffPhoneValue = (value?: string | null) => {
  if (!value) return null;
  return formatPhoneDisplay(value) ?? value;
};

const extractDetailedError = (payload: any): string | null => {
  if (!payload) return null;
  const candidates = [
    payload?.response?.error,
    payload?.response?.message,
    payload?.response?.payload?.error,
    payload?.payload?.error,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }

  return null;
};

const buildCurlCommand = (accountId: number | null | undefined, payload: any) => {
  if (!accountId) return null;
  const url = `${CHATWOOT_BASE_URL}/api/v1/accounts/${accountId}/contacts`;
  const body = JSON.stringify(payload, null, 2);
  return `curl -X POST \
  '${url}' \
  -H 'Content-Type: application/json' \
  -H 'api_access_token: <CHATWOOT_PLATFORM_TOKEN>' \
  -d '${body.replace(/'/g, "'\\''")}'`;
};

function useAuditEvents(filters: Filters) {
  const params = useMemo(() => {
    const query: Record<string, string> = {};
    if (filters.tenantId) query.tenant_id = filters.tenantId;
    if (filters.entityType) query.entity_type = filters.entityType;
    if (filters.action) query.action = filters.action;
    if (filters.direction) query.direction = filters.direction;
    if (filters.status) query.status = filters.status;
    if (filters.search) query.search = filters.search.trim();
    if (filters.dateRange?.from) query.from = filters.dateRange.from.toISOString();
    if (filters.dateRange?.to) query.to = filters.dateRange.to.toISOString();
    return query;
  }, [filters]);

  return useQuery({
    queryKey: ["chatwoot-sync-events", params],
    queryFn: async () => {
      const request = supabase
        .from("chatwoot_sync_events")
        .select(
          "id, created_at, tenant_id, entity_type, entity_id, chatwoot_contact_id, action, direction, status, payload, error_message"
        )
        .order("created_at", { ascending: false })
        .limit(100);

      if (params.tenant_id) request.eq("tenant_id", params.tenant_id);
      if (params.entity_type) request.eq("entity_type", params.entity_type);
      if (params.action) request.eq("action", params.action);
      if (params.direction) request.eq("direction", params.direction);
      if (params.status) request.eq("status", params.status);
      if (params.from) request.gte("created_at", params.from);
      if (params.to) request.lte("created_at", params.to);

      if (params.search) {
        request.or(
          `entity_id.ilike.%${params.search}%,` +
            `payload::text.ilike.%${params.search}%,` +
            `error_message.ilike.%${params.search}%`
        );
      }

      const { data, error } = await request;
      if (error) throw error;

      const events = data as EventRow[];

      // Buscar dados adicionais (nome/email/telefone)
      const groupedByType = events.reduce<Record<string, Set<string>>>((map, evt) => {
        if (!evt.entity_type || !evt.entity_id) return map;
        if (!map[evt.entity_type]) map[evt.entity_type] = new Set();
        map[evt.entity_type].add(evt.entity_id);
        return map;
      }, {});

      const fetches: Promise<any>[] = [];
      if (groupedByType.client?.size) {
        fetches.push((async () => {
          return await supabase
            .from("clients")
            .select("id, nome, telefone, email")
            .in("id", Array.from(groupedByType.client));
        })());
      }
      if (groupedByType.supplier?.size) {
        fetches.push((async () => {
          return await supabase
            .from("suppliers")
            .select("id, nome, telefone, email")
            .in("id", Array.from(groupedByType.supplier));
        })());
      }
      if (groupedByType.profile?.size) {
        fetches.push((async () => {
          return await supabase
            .from("profiles")
            .select("id, nome_completo, telefone, email")
            .in("id", Array.from(groupedByType.profile));
        })());
      }

      const results = await Promise.all(fetches);
      const contactMap = new Map<string, { name: string | null; email: string | null; phone: string | null }>();
      results.forEach((res) => {
        if (res?.data) {
          res.data.forEach((row: any) => {
            if (row.nome) {
              contactMap.set(row.id, { name: row.nome, email: row.email ?? null, phone: row.telefone ?? null });
            } else if (row.nome_completo) {
              contactMap.set(row.id, { name: row.nome_completo, email: row.email ?? null, phone: row.telefone ?? null });
            }
          });
        }
      });

      const tenantIds = Array.from(new Set(events.map((event) => event.tenant_id).filter(Boolean))) as string[];
      let tenantAccountMap = new Map<string, number | null>();
      if (tenantIds.length > 0) {
        const { data: tenantRows } = await supabase
          .from("tenants" as any)
          .select("id, chatwoot_account_id")
          .in("id", tenantIds);
        tenantRows?.forEach((tenant: any) => {
          tenantAccountMap.set(tenant.id, tenant.chatwoot_account_id ?? null);
        });
      }

      const sortedEvents = [...events].sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      return sortedEvents.map((event) => {
        const contactInfo = event.entity_id ? contactMap.get(event.entity_id) : undefined;
        return {
          ...event,
          contact_name: contactInfo?.name ?? null,
          contact_email: contactInfo?.email ?? null,
          contact_phone: contactInfo?.phone ?? null,
          chatwootAccountId: tenantAccountMap.get(event.tenant_id ?? "") ?? null,
        };
      });
    },
  });
}

function DiffBlock({ label, oldValue, newValue }: { label: string; oldValue?: string | null; newValue?: string | null }) {
  if (!oldValue && !newValue) return null;
  const equal = oldValue === newValue;
  if (equal) {
    return (
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <p className="text-xs text-muted-foreground">{label} (anterior)</p>
          <div className="rounded border bg-muted px-2 py-1">{oldValue || "-"}</div>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{label} (atual)</p>
          <div className="rounded border bg-muted px-2 py-1">{newValue || "-"}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2 text-sm">
      <div>
        <p className="text-xs text-muted-foreground">{label} (anterior)</p>
        <div className="rounded border bg-red-50 px-2 py-1 text-red-700">{oldValue || "-"}</div>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label} (atual)</p>
        <div className="rounded border bg-emerald-50 px-2 py-1 text-emerald-700">{newValue || "-"}</div>
      </div>
    </div>
  );
}

function formatDateTime(value: string) {
  try {
    return format(new Date(value), "dd/MM/yyyy HH:mm:ss", { locale: ptBR });
  } catch {
    return value;
  }
}

function formatDateOnly(date?: Date) {
  if (!date) return "";
  try {
    return format(date, "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "";
  }
}

export default function ChatwootAudit() {
  const { tenantId, isAdminSaas } = useTenant();
  const [filters, setFilters] = useState<Filters>(() => ({
    tenantId: isAdminSaas ? undefined : tenantId || undefined,
  }));
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const { data: tenantOptions = [], isLoading: loadingTenants } = useTenantsList(isAdminSaas);
  const { data: events = [], isLoading, refetch, isRefetching } = useAuditEvents(filters);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAdminSaas && tenantId) {
      setFilters((prev) => ({ ...prev, tenantId }));
    }
  }, [tenantId, isAdminSaas]);

  useEffect(() => {
    const channel = supabase
      .channel("chatwoot-sync-events-listener")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "chatwoot_sync_events",
        },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  const reprocessMutation = useMutation({
    mutationFn: async (row: EventRow) => {
      if (!row.entity_type || !row.entity_id) throw new Error("Evento sem entidade vinculada");
      const sdkResponse = await supabase.functions.invoke("chatwoot-sync-contact", {
        body: {
          entity_type: row.entity_type,
          entity_id: row.entity_id,
        },
      });
      if (sdkResponse.error) throw sdkResponse.error;
      if (sdkResponse.data?.error) throw new Error(sdkResponse.data.error);
      return sdkResponse.data;
    },
    onSuccess: async () => {
      toast.success("Sincronização reenviada com sucesso");
      queryClient.invalidateQueries({
        predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === "chatwoot-sync-events",
      });
      await refetch();
      await new Promise((resolve) => setTimeout(resolve, 1500));
      await refetch();
    },
    onError: (err: any) => {
      toast.error(err?.message || "Falha ao reenviar sincronização");
    },
  });

  const handleDateSelect = (range: DateRange | undefined) => {
    setFilters((prev) => ({ ...prev, dateRange: range }));
  };

  const tenantsSelectOptions = tenantOptions;
  const allTenantsOptions = [{ value: "", label: "Todos os tenants" }, ...tenantsSelectOptions];
  const entityOptions = [
    { value: "", label: "Todas" },
    { value: "client", label: "Cliente" },
    { value: "supplier", label: "Fornecedor" },
    { value: "profile", label: "Usuário" },
  ];
  const statusOptions = [
    { value: "", label: "Todos" },
    { value: "success", label: "Sucesso" },
    { value: "error", label: "Erro" },
    { value: "ignored", label: "Ignorado" },
  ];
  const actionOptions = [
    { value: "", label: "Todas" },
    { value: "create", label: "Criação" },
    { value: "update", label: "Atualização" },
    { value: "webhook", label: "Webhook" },
    { value: "reconcile", label: "Reprocesso" },
  ];
  const directionOptions = [
    { value: "", label: "Todas" },
    { value: "outbound", label: "Saída" },
    { value: "webhook", label: "Webhook" },
    { value: "reconcile", label: "Reprocesso" },
  ];

  return (
    <div className="space-y-6" data-cy="chatwoot-audit-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Auditoria Chatwoot</h1>
          <p className="text-muted-foreground text-sm">
            Consulte eventos de sincronização em tempo real, incluindo diffs de telefone e email.
          </p>
        </div>
        <Button onClick={() => refetch()} variant="outline" disabled={isRefetching}>
          <RefreshCw className={cn("h-4 w-4 mr-2", isRefetching && "animate-spin")} />
          Atualizar
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-5">
          {isAdminSaas && (
            <div className="md:col-span-2">
              <SearchableSelect
                label="Tenant"
                value={filters.tenantId ?? ""}
                onChange={(value) => setFilters((prev) => ({ ...prev, tenantId: value || undefined }))}
                options={allTenantsOptions}
                placeholder={loadingTenants ? "Carregando..." : "Selecione o tenant"}
              />
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">Entidade</label>
            <SearchableSelect
              value={filters.entityType ?? ""}
              onChange={(value) => setFilters((prev) => ({ ...prev, entityType: value || undefined }))}
              options={entityOptions}
              placeholder="Todas"
              withBackdrop={false}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">Status</label>
            <SearchableSelect
              value={filters.status ?? ""}
              onChange={(value) => setFilters((prev) => ({ ...prev, status: value || undefined }))}
              options={statusOptions}
              placeholder="Todos"
              withBackdrop={false}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">Ação</label>
            <SearchableSelect
              value={filters.action ?? ""}
              onChange={(value) => setFilters((prev) => ({ ...prev, action: value || undefined }))}
              options={actionOptions}
              placeholder="Todas"
              withBackdrop={false}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">Direção</label>
            <SearchableSelect
              value={filters.direction ?? ""}
              onChange={(value) => setFilters((prev) => ({ ...prev, direction: value || undefined }))}
              options={directionOptions}
              placeholder="Todas"
              withBackdrop={false}
            />
          </div>

          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">Busca</label>
            <Input
              value={filters.search || ""}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              placeholder="Buscar por telefone, email ou ID"
            />
          </div>

          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">Período</label>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  {filters.dateRange?.from ? (
                    <span>
                      {formatDateOnly(filters.dateRange.from)}
                      {filters.dateRange?.to ? ` até ${formatDateOnly(filters.dateRange.to)}` : ""}
                    </span>
                  ) : (
                    <span>Selecione uma data</span>
                  )}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={filters.dateRange}
                  onSelect={handleDateSelect}
                  initialFocus
                  locale={ptBR}
                />
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Resultados</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-10 w-2/3" />
            </div>
          ) : events.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Nenhum evento encontrado para os filtros selecionados.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entidade/Data</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead className="min-w-[180px]">Telefone/Email</TableHead>
                  <TableHead>Ação/Direção</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((row) => {
                  const isExpanded = expandedRowId === row.id;
                  return (
                    <Fragment key={row.id}>
                      <TableRow>
                        <TableCell className="text-sm w-[130px]">
                          <div className="flex flex-col gap-1">
                            {row.entity_type ? (
                              <Badge variant="secondary">{entityLabels[row.entity_type]}</Badge>
                            ) : (
                              <Badge variant="outline">N/A</Badge>
                            )}
                            <span className="text-xs text-muted-foreground">{formatDateTime(row.created_at)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm w-auto">
                          <div className="flex flex-col">
                            <span className="font-medium">{row.contact_name || "-"}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm w-[190px]">
                          <span>{formatPhoneDisplay(row.contact_phone) || "-"}</span>
                          {row.contact_email && (
                            <span className="block text-xs text-muted-foreground">{row.contact_email}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm w-[120px]">
                          <span>{row.action ? actionLabels[row.action] || row.action : "-"}</span>
                          {row.direction && (
                            <span className="block text-xs text-muted-foreground">
                              {directionLabels[row.direction] || row.direction}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm  w-[75px]">
                          <Badge variant={statusVariants[row.status]?.variant || "secondary"}>
                            {statusVariants[row.status]?.label || row.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm w-[120px]">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedRowId(isExpanded ? null : row.id)}
                          >
                            {isExpanded ? "Ocultar" : "Ver"}
                          </Button>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow className="bg-muted/30">
                          <TableCell colSpan={9}>
                            <div className="space-y-4 text-sm">
                              {(row.error_message || extractDetailedError(row.payload)) && (
                                <div className="flex flex-wrap items-start gap-3 text-destructive">
                                  <div className="flex items-start gap-2">
                                    <AlertCircle className="h-4 w-4 mt-0.5" />
                                    <span>{row.error_message}</span>
                                  </div>
                                  {extractDetailedError(row.payload) && (
                                    <span className="ml-auto text-sm font-medium text-destructive/80">
                                      {extractDetailedError(row.payload)}
                                    </span>
                                  )}
                                </div>
                              )}
                              <div className="grid gap-3 md:grid-cols-2">
                                <DiffBlock
                                  label="Telefone"
                                  oldValue={getDiffPhoneValue(
                                    row.payload?.previous?.phone ??
                                      row.payload?.existing?.phone_number ??
                                      row.payload?.existing?.payload?.[0]?.phone_number
                                  )}
                                  newValue={getDiffPhoneValue(
                                    row.payload?.contactPayload?.phone_number ||
                                      row.payload?.response?.payload?.phone_number ||
                                      row.payload?.payload?.phone_number
                                  )}
                                />
                                <DiffBlock
                                  label="Email"
                                  oldValue={
                                    row.payload?.previous?.email ??
                                    row.payload?.existing?.email ??
                                    row.payload?.existing?.payload?.[0]?.email
                                  }
                                  newValue={
                                    row.payload?.contactPayload?.email ||
                                    row.payload?.response?.payload?.email ||
                                    row.payload?.payload?.email
                                  }
                                />
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Payload completo</p>
                                <pre className="mt-1 max-h-64 w-full overflow-auto rounded border bg-muted p-3 text-xs">
                                  {JSON.stringify(row.payload, null, 2)}
                                </pre>
                              </div>
                              <div className="space-y-2 text-xs">
                                <div>
                                  <p className="font-medium text-muted-foreground">Endpoint</p>
                                  <code className="block rounded border bg-muted/60 p-2 text-[11px]">
                                    {`${CHATWOOT_BASE_URL}/api/v1/accounts/${row.chatwootAccountId ?? "{account_id}"}/contacts`}
                                  </code>
                                </div>
                                <div>
                                  <p className="font-medium text-muted-foreground">Headers</p>
                                  <pre className="rounded border bg-muted/60 p-2 text-[11px]">
{`Content-Type: application/json
api_access_token: <CHATWOOT_PLATFORM_TOKEN>`}
                                  </pre>
                                </div>
                                {buildCurlCommand(row.chatwootAccountId, row.payload?.contactPayload) && (
                                  <div>
                                    <p className="font-medium text-muted-foreground">curl</p>
                                    <pre className="rounded border bg-muted/60 p-2 text-[11px] whitespace-pre-wrap">
                                      {buildCurlCommand(row.chatwootAccountId, row.payload?.contactPayload)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    const endpoint = `${CHATWOOT_BASE_URL}/api/v1/accounts/${row.chatwootAccountId ?? "{account_id}"}/contacts`;
                                    const headers = `Content-Type: application/json\napi_access_token: <CHATWOOT_PLATFORM_TOKEN>`;
                                    const curl = buildCurlCommand(row.chatwootAccountId, row.payload?.contactPayload);
                                    const context = [
                                      "Endpoint:",
                                      endpoint,
                                      "",
                                      "Headers:",
                                      headers,
                                      "",
                                      "Payload:",
                                      JSON.stringify(row.payload, null, 2),
                                      curl ? `\ncurl:\n${curl}` : "",
                                    ]
                                      .filter(Boolean)
                                      .join("\n");
                                    navigator.clipboard.writeText(context);
                                    toast.success("Contexto copiado para a área de transferência");
                                  }}
                                >
                                  Copiar contexto
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(JSON.stringify(row.payload, null, 2));
                                    toast.success("Payload copiado para a área de transferência");
                                  }}
                                >
                                  Copiar JSON
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={reprocessMutation.isPending}
                                  onClick={() => reprocessMutation.mutate(row)}
                                >
                                  Reprocessar sincronização
                                </Button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
